import { Button, Space, Switch, Tag, Upload } from '@bedrock/components';
import { TagColor } from '@bedrock/components/lib/Tag';
import DotDotDot from '@mpc/common/src/components/DotDotDot';
import CopyableNode from '@mpc/common/src/components/CopyableNode';
import moment from 'moment';
import { AdvancePriceCheck, enums } from 'services/api';
import React from 'react';
import CurrencyType from '@mpc/common/src/components/SForm/SFormElement/CurrencyType';
import _ from 'lodash';
import { MPCListProps } from '@mpc/common/src/features/MPCList';
import AmountOrder from 'mpc-common/components/AmountOrder';

/** 运行时列表项可能携带 permit_actions / biz_id / biz_type（后端跨表返回），自动生成类型未收录 */
type AdvancePriceCheckListItem = AdvancePriceCheck & {
  biz_id?: number | string;
  biz_type?: string;
  permit_actions?: { can_upload?: boolean; can_move?: boolean; [key: string]: any };
};

export enum FIRST_LEVEL_ENUM {
  outNormalPurchase = 'outNormalPurchase',
  outBiddingPurchase = 'outBiddingPurchase',
  outPrepayment = 'outPrepayment',
  outAdvancePriceCheck = 'outAdvancePriceCheck',
  outRechargeRefund = 'outRechargeRefund',
}

const demandCustomColumnsConf = ({ handleDealChange, secondLevel, onInitRecharge, onInitRefund, rechargeActionLoadingId, refundActionLoadingId }: any = {}) => {
  return {
    plan_demand_id: {
      fixed: 'left',
      width: 96,
      render: (v: any, r: any) => {
        return <CopyableNode value={v} tipToolTitle="复制单号" />;
      },
    },
    leaf_demand_id: {
      fixed: 'left',
      width: 96,
      render: (v: any, r: any) => {
        return <CopyableNode value={v} tipToolTitle="复制单号" />;
      },
    },
    id: {
      width: 104,
      fixed: 'left',
      render: (v: any, r: any, i: number) => {
        return <CopyableNode value={v} tipToolTitle="复制单号" />;
      },
    },
    state_text: {
      width: 120,
      render: (v: any, r: any, i: number) => {
        return <div className="fw-bold">{v}</div>;
      },
    },
    name: {
      width: 360,
      render: (v: any, r: any, i: number) => {
        return (
          <div className="one-rows-ellipsis" title={v}>
            {r.backed ? <Tag color="light-red" size="xSmall" text="被退回" className="m-r-4" /> : null}
            {v}
          </div>
        );
      },
    },
    quote_deadline: {
      align: 'left',
      width: 268,
      render: (v: any, r: any, i: number) => {
        const diffHours = moment(v).diff(moment().startOf('days'), 'hours');
        let info = '';
        let color: TagColor = 'light-grey';
        if (diffHours < 0) {
          info = '已过期';
          color = 'light-grey';
        } else if (diffHours >= 0 && diffHours < 24) {
          info = '今天过期';
          color = 'light-red';
        } else if (diffHours >= 24) {
          const diffDays = _.round(diffHours / 24, 0);
          if (diffDays <= 3) {
            info = `剩余${diffDays}天过期`;
            color = 'light-orange';
          }
        }
        return (
          <Space size="xxs">
            {v}
            {info ? <Tag size="xSmall" color={color} text={info} /> : null}
          </Space>
        );
      },
    },
    finished: {
      width: 160,
      align: 'left',
      fixed: 'right',
      render: (v: any, r: any, i: number) => {
        return (
          <Space
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
            }}>
            <Switch
              checked={!v}
              labelOn="待处理"
              labelOff="已处理"
              onChange={(e: any) => {
                handleDealChange({
                  finished: !r.finished,
                  id: r.id,
                });
              }}
            />
          </Space>
        );
      },
    },
    quote_price: {
      fixed: [DEMAND_TAB_KEY.settledUpload, DEMAND_TAB_KEY.pendingQuote].includes(secondLevel) ? undefined : 'right',
      width: 170,
      align: 'right',
      render: (v: any, r: any, i: number) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: r?.quote_price_currency,
            }}
            currencyColor={'black'}
          />
        );
      },
    },
    settle_price: {
      fixed: 'right',
      width: 170,
      align: 'right',
      render: (v: any, r: any, i: number) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: r?.settle_price_currency,
            }}
          />
        );
      },
    },
    operation: {
      fixed: 'right',
      // 追加"发起充值/发起退款"后需容纳多个 link 按钮，MPCList 对 operation 列默认宽度仅 52px；
      // 仅「全部」tab 展示充值/退款，其余 tab 只留"处理"按钮，收窄列宽避免留白过宽
      width: secondLevel === DEMAND_TAB_KEY.allDemand ? 180 : 80,
      render: (v: any, r: any, i: number) => {
        // 按钮显隐由后端返回的 can_init_recharge / can_init_refund 标志位决定（适用范围+节点状态+是否已充值 由后端统一判定），
        // 与内包 QuoteInfoTable / BudgetDetailTable 一致；后端仅控制显隐，提交权限仍由发起页后端校验。
        // 充值/退款入口仅在「全部」tab 暴露（产品要求），其余 3 个常规采购 tab 不展示，即便后端标志位为 true。
        const showRechargeRefund = secondLevel === DEMAND_TAB_KEY.allDemand;
        return (
          <Space>
            {r?.permit_actions?.can_move && <Button type="link">处理</Button>}
            {showRechargeRefund && r?.can_init_recharge && (
              <Button
                type="link"
                // 防重复点击：不用 loading（图标占位致列表抖动），改 disabled 用当前行不可点
                disabled={rechargeActionLoadingId === Number(r?.plan_demand_id) || refundActionLoadingId === Number(r?.plan_demand_id)}
                onClick={(e) => {
                  // 阻止冒泡到行，避免触发行点击打开常规采购详情页；inline 打开充值抽屉（不跳路由，避免工作台状态丢失）
                  e.stopPropagation();
                  onInitRecharge?.(r);
                }}>
                充值
              </Button>
            )}
            {showRechargeRefund && r?.can_init_refund && (
              <Button
                type="link"
                disabled={rechargeActionLoadingId === Number(r?.plan_demand_id) || refundActionLoadingId === Number(r?.plan_demand_id)}
                onClick={(e) => {
                  // 阻止冒泡到行，避免触发行点击打开详情页；inline 打开退款发起抽屉（不改变 URL）
                  e.stopPropagation();
                  onInitRefund?.(r);
                }}>
                退款
              </Button>
            )}
          </Space>
        );
      },
    },
  };
};

export enum DEMAND_TAB_KEY {
  pendingQuote = 'pendingQuotePortal',
  making = 'makingPortal',
  settledUpload = 'settledUploadPortal',
  allDemand = 'allDemandPortal',
}

export enum PREPAYMENT_TAB_KEY {
  prepaymentUpload = 'prepaymentUpload',
  allPrepayment = 'allPrepayment',
}

export enum RECHARGE_REFUND_TAB_KEY {
  todo = 'outRechargeRefundTodo',
  all = 'outRechargeRefundAll',
}

export const demandTabItems = [
  {
    key: DEMAND_TAB_KEY.pendingQuote,
    tab: '待报价',
  },
  {
    key: DEMAND_TAB_KEY.making,
    tab: '待制作',
  },
  {
    key: DEMAND_TAB_KEY.settledUpload,
    tab: '发票待上传',
  },
  {
    key: DEMAND_TAB_KEY.allDemand,
    tab: '全部',
  },
];

const demandFilterOptions: MPCListProps['filterOptions'] = {
  filterKeys: [
    {
      searchKey: 'state_list',
      optionKey: 'wb_demand_states',
    },
    {
      searchKey: 'product',
      optionKey: 'wb_new_products',
    },
    {
      searchKey: 'demand_categories',
      optionKey: 'demand_categories',
    },
    {
      searchKey: 'quote_deadline_range',
      optionKey: 'quote_deadline_range',
      name: '报价截止时间',
    },
    {
      searchKey: 'expect_end_at_range',
      optionKey: 'expect_end_at_range',
      name: '预计发布时间',
    },
    {
      searchKey: 'backed',
      optionKey: 'wb_demand_back',
      name: '仅展示被退回',
    },
  ],
};

const biddingCustomColumnsConf = {
  id: {
    fixed: 'left',
    width: 96,
    render: (v: any, r: any) => {
      return (
        <CopyableNode value={v} tipToolTitle="复制单号">
          <Button type="link">{v}</Button>
        </CopyableNode>
      );
    },
  },
  product_identifier: {
    width: 104,
    align: 'left',
  },
  name: {
    width: 240,
    align: 'left',
    render: (v: any) => <DotDotDot clamp={1}>{v || '-'}</DotDotDot>,
  },
  budget_text: {
    width: 200,
    align: 'right',
    render: (v: string, r: { budget: any; currency_enum: any }) => (
      <CurrencyType
        view
        value={{
          value: r?.budget,
          currency: r?.currency_enum,
        }}
        currencyColor={'black'}
      />
    ),
  },
  // budget_text: {
  //   width: 200,
  //   align: 'right',
  //   render: (v: string) => (
  //     <div className="fw-bold" style={{ display: 'inline-grid'}}>
  //       <div>{v}</div>
  //       <div style={{ justifySelf: 'start' }}>
  //         <AmountOrder value={v?.split(' ')[0]} />
  //       </div>
  //     </div>
  //   )
  // },
  bidding_method: {
    width: 120,
    align: 'left',
  },
  bidding_type: {
    width: 160,
    align: 'left',
  },
  businesses_names: {
    align: 'left',
    width: 160,
    render: (v: any, r: any) => {
      const text = Array.isArray(v) ? v.join('，') : v;
      return <DotDotDot clamp={1}>{text || '-'}</DotDotDot>;
    },
  },
  bidding_state_text: {
    width: 200,
    align: 'left',
  },
  operation: {
    width: 60,
    fixed: 'right',
    render: (v: any, r: any) => {
      return (
        (r['can_move?'] || r['can_move']) &&
        (r['state'] || '').toLowerCase() !== 'closed' && <Button type="link">处理</Button>
      );
    },
  },
};

const prepaymentColumnsConf = {
  prepayment_no: {
    fixed: 'left',
    width: 140,
    fieldOption: { locked: true },
    sortable: true,
    render: (v: any, r: any, i: number) => {
      return (
        <CopyableNode value={v} tipToolTitle="复制单号">
          <Button type="link">{v}</Button>
        </CopyableNode>
      );
    },
  },
  // 来源单号
  demand_id: {
    width: 140,
    title: '来源单号',
    fieldOption: { locked: true },
    sortable: true,
    render: (v: any, r: any, i: number) => {
      return (
        <CopyableNode value={v} tipToolTitle="复制单号">
          <Button type="link">{v}</Button>
        </CopyableNode>
      );
    },
  },
  invoice_method_desc: {
    width: 120,
    align: 'left',
  },
  // 所属产品
  product_name: {
    width: 96,
    align: 'left',
    render: (v) => <div className="two-rows-ellipsis">{v || '--'}</div>,
  },
  // 需求标题
  demand_title: {
    width: 320,
    align: 'left',
    render: (v) => (
      <div className="two-rows-ellipsis" title={v}>
        {v || '--'}
      </div>
    ),
  },
  // 需求类型
  demand_type_desc: {
    width: 240,
    render: (v) => (
      <div className="two-rows-ellipsis" title={v}>
        {v || '--'}
      </div>
    ),
  },
  // 预付金额
  prepayment_amount: {
    width: 200,
    fixed: 'right',
    sortable: true,
    render: (v, r) => (
      <CurrencyType
        view
        value={{
          value: v,
          currency: r?.currency,
        }}
      />
    ),
  },
  operation: {
    width: 60,
    fixed: 'right',
    fieldOption: { locked: true },
    render: (v: any, r: any) => {
      return (
        <Space>
          {r?.permit_actions?.can_move ? <Button type="link">处理</Button> : <Button type="link">查看</Button>}
        </Space>
      );
    },
  },
};

export enum BIDDING_TAB_KEY {
  toDoBidding = 'toDoBidding',
  doneBidding = 'doneBidding',
}

export const biddingTabItems = [
  {
    key: BIDDING_TAB_KEY.toDoBidding,
    tab: '待办',
  },
  {
    key: BIDDING_TAB_KEY.doneBidding,
    tab: '已办',
  },
];

const biddingFilterOptions = {
  filterKeys: [
    // {
    //   searchKey: 'state',
    //   optionKey: 'state',
    // },
    // {
    //   searchKey: 'product',
    //   optionKey: 'wb_prdoucts'
    // },
    {
      searchKey: 'bidding_types',
      optionKey: 'bidding_types',
    },
    {
      searchKey: 'bidding_methods',
      optionKey: 'bidding_methods',
    },
    // {
    //   searchKey: 'invite_confirm_end_time_range',
    //   optionKey: 'invite_confirm_end_time_range'
    // },
    // {
    //   searchKey: 'submit_plan_end_time_range',
    //   optionKey: 'submit_plan_end_time_range'
    // },
  ],
};

const prepaymentFilterOptions = {
  filterKeys: [
    {
      searchKey: 'status_list',
      optionKey: 'os_prepayment_status',
      name: '状态',
    },
    {
      searchKey: 'invoice_method_list',
      optionKey: 'invoice_method',
      name: '发票方式',
    },
    {
      searchKey: 'demand_category_ids',
      optionKey: 'demand_categories',
    },
    {
      searchKey: 'product_id_list',
      optionKey: 'wb_prepayment_products',
    },
  ],
};

const advancePriceCheckColumnsConf = ({
  onProcess,
}: {
  onProcess?: (r: AdvancePriceCheckListItem) => void;
}) => {
  return {
    // 来源单号
    biz_number: {
      fixed: 'left',
      width: 104,
      fieldOption: { locked: true },
      sortable: true,
      render: (v: any, r: any, i: number) => {
        return (
          <CopyableNode value={r.biz_id} tipToolTitle="复制单号">
            <Button type="link">{r.biz_id}</Button>
          </CopyableNode>
        );
      },
    },
    // 所属产品
    product_code: {
      width: 88,
      align: 'left',
      render: (v) => <div className="two-rows-ellipsis">{v || '--'}</div>,
    },
    // 需求标题
    demand_title: {
      width: 582,
      align: 'left',
      render: (v) => (
        <div className="two-rows-ellipsis" title={v}>
          {v || '--'}
        </div>
      ),
    },
    file_name: {
      width: 320,
      align: 'left',
      render: (v: any, r: AdvancePriceCheck) => {
        if (!r.file_name) return '--';
        return (
          <Upload
            className="advance-price-check-upload"
            fileList={[{ uid: '1', name: r.file_name, url: '' }]}
            showRemoveIcon={false}
            showDownloadIcon={false}
            trigger={<></>}
          />
        );
      },
    },
    price_check_state: {
      width: 120,
      align: 'left',
      render: (v: any, r: AdvancePriceCheck) => {
        const state = (v || 'NOT_UPLOADED') as keyof typeof stateMap;
        //状态：AI_CHECKING=智能核价中 PENDING_CONFIRM=待商务确认核价 CONFIRMED=已核价 VOIDED=已作废 PENDING_PLAN_CONFIRM_DARU=待策划确认达人 CLOSED=已关闭 NOT_UPLOADED=未上传 */
        const stateMap = {
          AI_CHECKING: {
            color: 'light-blue',
            text: '核价中',
          },
          PENDING_CONFIRM: {
            color: 'light-blue',
            text: '待商务确认核价',
          },
          PENDING_PLAN_CONFIRM_DARU: {
            color: 'light-blue',
            text: '待策划确认达人',
          },
          CONFIRMED: {
            color: 'light-green',
            text: '已核价',
          },
          VOIDED: {
            color: 'light-red',
            text: '已作废',
          },
          CLOSED: {
            color: 'light-grey',
            text: '已关闭',
          },
          NOT_UPLOADED: {
            color: 'light-grey',
            text: '未上传',
          },
        };

        return <Tag color={stateMap[state]?.color as TagColor} text={stateMap[state]?.text} size="small" />;
      },
    },
    operation: {
      width: 120,
      fixed: 'right',
      fieldOption: { locked: true },
      render: (_v: any, r: AdvancePriceCheckListItem) => {
        return (
          <Space
            onClick={(e) => {
              // 避免与行点击冒泡冲突（行点击已经会打开抽屉）
              e.stopPropagation();
            }}
          >
            <Button type="link" onClick={() => onProcess?.(r)}>
              处理
            </Button>
          </Space>
        );
      },
    },
  };
};

const advancePriceCheckFilterOptions = {
  filterKeys: [
    {
      searchKey: 'product_ids',
      optionKey: 'wb_kol_quote_products',
      name: '所属产品',
    },
    {
      searchKey: 'price_check_states',
      optionKey: 'advance_state',
      name: '状态',
    },
  ],
};

const rechargeRefundCustomColumnsConf = ({ onDetail }: any = {}) => {
  return {
    order_no: {
      fixed: 'left',
      width: 140,
      render: (v: any, r: any) => {
        return <CopyableNode value={v} tipToolTitle="复制单号" />;
      },
    },
    order_type_desc: {
      width: 120,
      align: 'left',
    },
    state_desc: {
      width: 120,
      render: (v: any, r: any) => {
        return <div className="fw-bold">{v}</div>;
      },
    },
    // 二级单号
    second_demand_id: {
      width: 120,
      render: (v: any, r: any) => {
        return v ? <CopyableNode value={v} tipToolTitle="复制单号" /> : '--';
      },
    },
    // 三级单号
    third_demand_id: {
      width: 120,
      render: (v: any, r: any) => {
        return v ? <CopyableNode value={v} tipToolTitle="复制单号" /> : '--';
      },
    },
    // 所属产品
    product_code: {
      width: 120,
      align: 'left',
    },
    // 需求标题
    demand_title: {
      width: 320,
      align: 'left',
      render: (v: any) => (
        <div className="two-rows-ellipsis" title={v}>
          {v || '--'}
        </div>
      ),
    },
    amount: {
      fixed: 'right',
      width: 170,
      align: 'right',
      render: (v: any, r: any) => {
        const isRefund = r?.order_type === 'REFUND';
        const displayAmount = isRefund ? Math.abs(v) : v;
        return (
          <CurrencyType
            view
            value={{
              value: displayAmount,
              currency: r?.currency,
            }}
            currencyColor={'black'}
          />
        );
      },
    },
    operation: {
      width: 90,
      fixed: 'right',
      render: (v: any, r: any) => {
        // 可操作（供应商确认态 can_confirm/can_reject、待发起态 can_abandon，后端 permit_actions 动态下发）
        // 显示"处理"（点击打开详情可确认/退回/废弃），否则"查看"
        const operable = !!r?.permit_actions?.can_confirm
          || !!r?.permit_actions?.can_reject
          || !!r?.permit_actions?.can_abandon;
        return (
          // stopPropagation：避免按钮点击冒泡到行点击（onClickRow 也会打开详情 → 两层抽屉）
          <Space
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Button type="link" onClick={() => onDetail?.(r)}>
              {operable ? '处理' : '查看'}
            </Button>
          </Space>
        );
      },
    },
  };
};

const rechargeRefundFilterOptions = {
  // 外包端充值/退款列表查询条件参考内包端，仅保留二级单号 / 三级单号
  // （单号搜索走 toolbar 搜索框 search_text，见 index.tsx toolbarSearch）
  filterKeys: [
    {
      searchKey: 'second_demand_id',
      name: '二级单号',
      filterComponentProps: { placeholder: '请输入二级单ID' },
      noFetchOption: true,
    },
    {
      searchKey: 'third_demand_id',
      name: '三级单号',
      filterComponentProps: { placeholder: '请输入三级单ID' },
      noFetchOption: true,
    },
  ],
};

export enum ADVANCE_PRICE_CHECK_TAB_KEY {
  all = 'all',
}

const omitFilter = (filterOptions: MPCListProps['filterOptions'], keys: string[]) => {
  const data = filterOptions?.filterKeys?.filter(
    (item) => !keys.includes(typeof item === 'string' ? item : item.searchKey),
  );
  return {
    filterKeys: data,
  };
};

/** 供应商端充值单状态文案 → Tag 颜色（SupplierWorkspaceVo.state_desc，§5.4.2 五态） */
const supplierStateColor = (desc?: string): TagColor => {
  if (!desc) return 'light-grey';
  if (desc.includes('已完成')) return 'light-green';
  if (desc.includes('废弃')) return 'light-grey';
  if (desc.includes('待发起')) return 'light-orange';
  return 'light-blue';
};

/** 充值单列表列配置（SupplierWorkspaceVo 字段，行点击默认走详情抽屉） */
const rechargeColumnsConf = {
  order_no: {
    fixed: 'left',
    width: 140,
    fieldOption: { locked: true },
    render: (v: any, r: any) => (
      <CopyableNode value={v} tipToolTitle="复制单号">
        <Button type="link">{v}</Button>
      </CopyableNode>
    ),
  },
  // 二级单号
  second_demand_id: {
    width: 120,
    align: 'left',
    render: (v: any) => <CopyableNode value={v} tipToolTitle="复制单号" />,
  },
  // 三级单号
  third_demand_id: {
    width: 120,
    align: 'left',
    render: (v: any) => (v ? <CopyableNode value={v} tipToolTitle="复制单号" /> : '--'),
  },
  // 需求标题
  demand_title: {
    width: 320,
    align: 'left',
    render: (v: any) => (
      <div className="two-rows-ellipsis" title={v}>
        {v || '--'}
      </div>
    ),
  },
  // 需求类型
  demand_category_name: {
    width: 160,
    align: 'left',
    render: (v: any) => <DotDotDot clamp={1}>{v || '--'}</DotDotDot>,
  },
  // 供应商端状态
  state_desc: {
    width: 120,
    align: 'left',
    render: (v: any, r: any) => (
      <Tag size="small" text={v || '--'} color={supplierStateColor(v)} />
    ),
  },
  // 充值金额
  amount: {
    width: 170,
    fixed: 'right',
    align: 'right',
    sortable: true,
    render: (v: any, r: any) => (
      <CurrencyType view value={{ value: v, currency: r?.currency }} />
    ),
  },
  operation: {
    width: 80,
    fixed: 'right',
    fieldOption: { locked: true },
    render: () => (
      <Button type="link">查看</Button>
    ),
  },
};

export const LIST_CONF = {
  [FIRST_LEVEL_ENUM.outNormalPurchase]: {
    [DEMAND_TAB_KEY.pendingQuote]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: omitFilter(demandFilterOptions, ['state_list']),
    },
    [DEMAND_TAB_KEY.making]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: omitFilter(demandFilterOptions, ['state_list', 'quote_deadline_range']),
    },
    [DEMAND_TAB_KEY.settledUpload]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: omitFilter(demandFilterOptions, ['state_list', 'quote_deadline_range', 'expect_end_at_range']),
    },
    [DEMAND_TAB_KEY.allDemand]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: demandFilterOptions,
    },
  },
  [FIRST_LEVEL_ENUM.outBiddingPurchase]: {
    [BIDDING_TAB_KEY.toDoBidding]: {
      customColumnsConf: () => biddingCustomColumnsConf,
      filterOptions: biddingFilterOptions,
    },
    [BIDDING_TAB_KEY.doneBidding]: {
      customColumnsConf: () => biddingCustomColumnsConf,
      filterOptions: biddingFilterOptions,
    },
  },
  [FIRST_LEVEL_ENUM.outPrepayment]: {
    [PREPAYMENT_TAB_KEY.prepaymentUpload]: {
      customColumnsConf: () => prepaymentColumnsConf,
      filterOptions: omitFilter(prepaymentFilterOptions, ['status_list']),
    },
    [PREPAYMENT_TAB_KEY.allPrepayment]: {
      customColumnsConf: () => prepaymentColumnsConf,
      filterOptions: prepaymentFilterOptions,
    },
  },
  [FIRST_LEVEL_ENUM.outAdvancePriceCheck]: {
    [ADVANCE_PRICE_CHECK_TAB_KEY.all]: {
      customColumnsConf: ({ onProcess }: any) => advancePriceCheckColumnsConf({ onProcess }),
      filterOptions: advancePriceCheckFilterOptions,
    },
  },
  [FIRST_LEVEL_ENUM.outRechargeRefund]: {
    [RECHARGE_REFUND_TAB_KEY.todo]: {
      customColumnsConf: ({ onDetail }: any) => rechargeRefundCustomColumnsConf({ onDetail }),
      filterOptions: rechargeRefundFilterOptions,
    },
    [RECHARGE_REFUND_TAB_KEY.all]: {
      customColumnsConf: ({ onDetail }: any) => rechargeRefundCustomColumnsConf({ onDetail }),
      filterOptions: rechargeRefundFilterOptions,
    },
  },
};